/**
 * Descriptive statistics for the campaign Overview. Pure functions with no
 * rounding: definitions match Postgres (`percentile_cont`, `stddev_samp`,
 * `corr`) so numbers reconcile with SQL checks. Rounding happens at render.
 */

/** One histogram bar. Value bins have `x0 === x1`; range bins are `[x0, x1)`, the last closed. */
export interface HistogramBin {
  x0: number;
  x1: number;
  count: number;
}

/** Histogram bars plus whether they are one bar per value or equal-width ranges. */
export interface Histogram {
  kind: 'values' | 'ranges';
  bins: HistogramBin[];
}

/** Arithmetic mean, or null without values. */
export function mean(values: readonly number[]): number | null {
  if (!values.length) return null;
  return values.reduce((sum, value) => sum + value, 0) / values.length;
}

/** Continuous quantile with linear interpolation (Postgres `percentile_cont`). */
export function quantile(values: readonly number[], p: number): number | null {
  if (!values.length) return null;
  const sorted = [...values].sort((a, b) => a - b);
  const position = p * (sorted.length - 1);
  const lower = Math.floor(position);
  const upper = Math.ceil(position);
  return sorted[lower] + (sorted[upper] - sorted[lower]) * (position - lower);
}

/** Median with linear interpolation, so an even count averages the middle pair. */
export function median(values: readonly number[]): number | null {
  return quantile(values, 0.5);
}

/** Sample standard deviation (`n - 1`, Postgres `stddev_samp`), or null below two values. */
export function sampleStdDev(values: readonly number[]): number | null {
  if (values.length < 2) return null;
  const average = mean(values)!;
  const squares = values.reduce((sum, value) => sum + (value - average) ** 2, 0);
  return Math.sqrt(squares / (values.length - 1));
}

/** Pearson r (Postgres `corr`); null below two pairs or when either side has no variance. */
export function pearson(xs: readonly number[], ys: readonly number[]): number | null {
  if (xs.length !== ys.length || xs.length < 2) return null;
  const meanX = mean(xs)!;
  const meanY = mean(ys)!;
  let covariance = 0;
  let varianceX = 0;
  let varianceY = 0;
  for (let index = 0; index < xs.length; index++) {
    const dx = xs[index] - meanX;
    const dy = ys[index] - meanY;
    covariance += dx * dy;
    varianceX += dx * dx;
    varianceY += dy * dy;
  }
  if (varianceX === 0 || varianceY === 0) return null;
  return covariance / Math.sqrt(varianceX * varianceY);
}

/** One-based ranks in input order; ties share the mean of the ranks they span. */
export function averageRanks(values: readonly number[]): number[] {
  const order = values.map((value, index) => ({ value, index })).sort((a, b) => a.value - b.value);
  const ranks = new Array<number>(values.length);
  for (let start = 0; start < order.length;) {
    let end = start;
    while (end + 1 < order.length && order[end + 1].value === order[start].value) end++;
    const rank = (start + end) / 2 + 1;
    for (let index = start; index <= end; index++) ranks[order[index].index] = rank;
    start = end + 1;
  }
  return ranks;
}

/** Spearman rho: Pearson r on average ranks. */
export function spearman(xs: readonly number[], ys: readonly number[]): number | null {
  if (xs.length !== ys.length) return null;
  return pearson(averageRanks(xs), averageRanks(ys));
}

/** Integer data with at most this many distinct values gets one bar per value. */
const MAX_VALUE_BINS = 12;
/** Upper bound on equal-width range bins. */
const MAX_RANGE_BINS = 10;
/** Nice step mantissas: 1, 2 or 5 times a power of ten. */
const NICE_MANTISSAS = [1, 2, 5] as const;

/** A step as mantissa times a power of ten, so edges multiply without float drift. */
interface NiceStep {
  mantissa: number;
  exponent: number;
}

/** Smallest nice step at or above the raw width. */
function niceStepAtLeast(raw: number): NiceStep {
  let exponent = Math.floor(Math.log10(raw));
  for (;;) {
    for (const mantissa of NICE_MANTISSAS) {
      if (scale(mantissa, exponent) >= raw) return { mantissa, exponent };
    }
    exponent++;
  }
}

/** The next larger nice step. */
function nextNiceStep(step: NiceStep): NiceStep {
  const index = NICE_MANTISSAS.indexOf(step.mantissa as typeof NICE_MANTISSAS[number]);
  return index < NICE_MANTISSAS.length - 1
    ? { mantissa: NICE_MANTISSAS[index + 1], exponent: step.exponent }
    : { mantissa: NICE_MANTISSAS[0], exponent: step.exponent + 1 };
}

/** Exact `units * 10^exponent` for integer units, dividing for negative exponents. */
function scale(units: number, exponent: number): number {
  return exponent >= 0 ? units * 10 ** exponent : units / 10 ** -exponent;
}

/**
 * Histogram bars: one per value for integer data with at most 12 distinct
 * values, otherwise at most 10 equal-width bins with nice edges covering min
 * to max (left-inclusive, last bin closed). Bars always start at zero count.
 */
export function histogramBins(values: readonly number[]): Histogram {
  if (!values.length) return { kind: 'values', bins: [] };
  const sorted = [...values].sort((a, b) => a - b);
  const distinct = [...new Set(sorted)];
  if (sorted.every(Number.isInteger) && distinct.length <= MAX_VALUE_BINS) {
    return { kind: 'values', bins: distinct.map(value => ({ x0: value, x1: value, count: sorted.filter(item => item === value).length })) };
  }
  const min = sorted[0];
  const max = sorted[sorted.length - 1];
  if (min === max) return { kind: 'ranges', bins: [{ x0: min, x1: max, count: sorted.length }] };
  let step = niceStepAtLeast((max - min) / MAX_RANGE_BINS);
  for (;;) {
    const width = scale(step.mantissa, step.exponent);
    const firstUnit = Math.floor(min / width) * step.mantissa;
    const count = Math.max(1, Math.ceil((max - scale(firstUnit, step.exponent)) / width - 1e-9));
    if (count <= MAX_RANGE_BINS) {
      const edges = Array.from({ length: count + 1 }, (_, index) => scale(firstUnit + index * step.mantissa, step.exponent));
      const bins = edges.slice(0, -1).map((x0, index) => ({ x0, x1: edges[index + 1], count: 0 }));
      for (const value of sorted) {
        const index = bins.findIndex((bin, position) => value >= bin.x0 && (value < bin.x1 || position === bins.length - 1));
        bins[index === -1 ? bins.length - 1 : index].count++;
      }
      return { kind: 'ranges', bins };
    }
    step = nextNiceStep(step);
  }
}
